import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Bug,
  RefreshCw,
  Copy,
  CheckCircle2,
  Trash2,
  EyeOff,
  RotateCcw,
  Search,
  Server,
  Globe,
  Filter,
  X,
} from "lucide-react";
import api from "../lib/api";

const STATUS_OPTIONS = [
  { value: "new", label: "New (unresolved)" },
  { value: "resolved", label: "Resolved" },
  { value: "ignored", label: "Ignored" },
  { value: "all", label: "All" },
];

const SOURCE_OPTIONS = [
  { value: "all", label: "All sources" },
  { value: "backend", label: "Backend" },
  { value: "frontend", label: "Frontend" },
];

const DAYS_OPTIONS = [
  { value: "0", label: "Any time" },
  { value: "1", label: "Last 24h" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
];

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("uk-UA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
};

const formatRelative = (iso) => {
  if (!iso) return "—";
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diffMin = Math.round((now - t) / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  return `${diffD}d ago`;
};

const StatusBadge = ({ status }) => {
  const map = {
    new: "bg-red-500/15 text-red-300 border-red-500/30",
    resolved: "bg-green-500/15 text-green-300 border-green-500/30",
    ignored: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  };
  return (
    <Badge className={`${map[status] || map.new} border text-[10px] uppercase`}>
      {status || "new"}
    </Badge>
  );
};

const SourceBadge = ({ source }) => {
  const isBackend = source === "backend";
  const Icon = isBackend ? Server : Globe;
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${isBackend ? "text-amber-300" : "text-blue-300"}`}>
      <Icon className="h-3 w-3" /> {source}
    </span>
  );
};

const ErrorLogsPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const [statusFilter, setStatusFilter] = useState("new");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [daysFilter, setDaysFilter] = useState("0");
  const [search, setSearch] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveNote, setResolveNote] = useState("");
  const [resolveAction, setResolveAction] = useState("resolve"); // resolve | ignore

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 200 };
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (statusFilter === "all") params.status = "all";
      if (sourceFilter && sourceFilter !== "all") params.source = sourceFilter;
      if (daysFilter && daysFilter !== "0") params.days = daysFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/errors", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error("[errors] list failed", e);
      toast.error("Failed to load errors");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter, daysFilter, search]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/errors/stats");
      setStats(data);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchList();
    fetchStats();
  }, [fetchList, fetchStats]);

  const openDetail = async (id) => {
    setSelectedId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/errors/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error("Failed to load error detail");
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
  };

  const copyForClaude = async (id) => {
    try {
      const { data } = await api.get(`/errors/${id}/copy`);
      await navigator.clipboard.writeText(data.text);
      toast.success("Copied — paste into Claude/ChatGPT to debug");
    } catch (e) {
      toast.error("Failed to copy");
    }
  };

  const copyRawText = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const performResolve = async () => {
    if (!selectedId) return;
    try {
      const url = resolveAction === "ignore" ? `/errors/${selectedId}/ignore` : `/errors/${selectedId}/resolve`;
      await api.post(url, { note: resolveNote || null });
      toast.success(resolveAction === "ignore" ? "Marked as ignored" : "Marked as resolved");
      setResolveOpen(false);
      setResolveNote("");
      closeDetail();
      fetchList();
      fetchStats();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const reopen = async (id) => {
    try {
      await api.post(`/errors/${id}/reopen`);
      toast.success("Reopened");
      if (selectedId === id) {
        const { data } = await api.get(`/errors/${id}`);
        setDetail(data);
      }
      fetchList();
      fetchStats();
    } catch (e) {
      toast.error("Failed to reopen");
    }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Delete this error log permanently?")) return;
    try {
      await api.delete(`/errors/${id}`);
      toast.success("Deleted");
      closeDetail();
      fetchList();
      fetchStats();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const cleanupResolved = async () => {
    if (!window.confirm("Delete all resolved/ignored errors older than 30 days?")) return;
    try {
      const { data } = await api.delete("/errors/cleanup/resolved", { params: { older_than_days: 30 } });
      toast.success(`Deleted ${data.deleted} old entries`);
      fetchList();
      fetchStats();
    } catch (e) {
      toast.error("Cleanup failed");
    }
  };

  const clearFilters = () => {
    setStatusFilter("new");
    setSourceFilter("all");
    setDaysFilter("0");
    setSearch("");
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Bug className="h-6 w-6 text-orange-400" />
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-zinc-50">Error Logs</h1>
            <p className="text-xs text-zinc-400">
              Centralized error tracker. Click any row → "Copy for Claude" to paste into a chat.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchList(); fetchStats(); }} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={cleanupResolved}>
            <Trash2 className="h-4 w-4 mr-1.5" /> Cleanup old
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Unresolved" value={stats.new} accent="text-red-300" />
          <StatCard label="Last 24h" value={stats.new_24h} accent="text-amber-300" />
          <StatCard label="Backend" value={stats.backend_new} accent="text-orange-300" />
          <StatCard label="Frontend" value={stats.frontend_new} accent="text-blue-300" />
        </div>
      )}

      {/* "How it works" hint */}
      <Card className="bg-zinc-900/40 border-zinc-800">
        <CardContent className="pt-4 text-xs text-zinc-400 space-y-1">
          <div className="flex items-start gap-2">
            <span className="text-orange-400 font-medium">Workflow:</span>
            <span>
              Bug reproduces → find it here → click <span className="text-zinc-200">"Copy for Claude"</span> →
              paste into a Claude/ChatGPT chat → ask to fix. The copied block contains stack trace, URL, user role,
              user-agent, and how often it's been seen — everything an LLM needs to debug.
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="bg-zinc-900/40 border-zinc-800">
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 text-zinc-400 text-xs">
              <Filter className="h-3.5 w-3.5" />
              <span>Filters:</span>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOURCE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={daysFilter} onValueChange={setDaysFilter}>
              <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DAYS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-zinc-500" />
              <Input
                placeholder="Search message..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 pl-7 w-[260px] text-xs"
              />
            </div>
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-xs">
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
            <div className="ml-auto text-xs text-zinc-500">
              {total} match{total === 1 ? "" : "es"}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card className="bg-zinc-900/40 border-zinc-800">
        <CardContent className="p-0">
          {loading && items.length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm">
              No errors match these filters. {statusFilter === "new" && "Nothing to fix — nice."}
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => openDetail(item.id)}
                  className="w-full text-left p-3 md:p-4 hover:bg-zinc-800/30 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusBadge status={item.status} />
                        <SourceBadge source={item.source} />
                        {item.count > 1 && (
                          <Badge className="bg-zinc-800 text-zinc-300 border-0 text-[10px]">
                            {item.count}×
                          </Badge>
                        )}
                        {item.latest_occurrence?.method && item.latest_occurrence?.url && (
                          <span className="text-[11px] text-zinc-500 font-mono truncate">
                            {item.latest_occurrence.method} {item.latest_occurrence.url}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-zinc-200 truncate">
                        {item.message}
                      </div>
                      <div className="mt-1 text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap">
                        <span>{formatRelative(item.last_seen)}</span>
                        {item.latest_occurrence?.user_email && (
                          <>
                            <span>·</span>
                            <span>{item.latest_occurrence.user_email}</span>
                          </>
                        )}
                        {item.latest_occurrence?.user_role && (
                          <span className="text-zinc-600">({item.latest_occurrence.user_role})</span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); copyForClaude(item.id); }}
                      className="text-xs flex-shrink-0"
                      title="Copy for Claude/ChatGPT"
                    >
                      <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                    </Button>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!selectedId} onOpenChange={(open) => !open && closeDetail()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-orange-400" />
              {detail ? "Error detail" : "Loading…"}
            </DialogTitle>
          </DialogHeader>
          {detailLoading || !detail ? (
            <div className="py-8 text-center text-zinc-500">Loading…</div>
          ) : (
            <ErrorDetail
              detail={detail}
              onCopyForClaude={() => copyForClaude(detail.id)}
              onCopyText={copyRawText}
              onResolve={() => { setResolveAction("resolve"); setResolveNote(""); setResolveOpen(true); }}
              onIgnore={() => { setResolveAction("ignore"); setResolveNote(""); setResolveOpen(true); }}
              onReopen={() => reopen(detail.id)}
              onDelete={() => deleteOne(detail.id)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Resolve / ignore note dialog */}
      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {resolveAction === "ignore" ? "Mark as ignored" : "Mark as resolved"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-zinc-400">
              {resolveAction === "ignore"
                ? "Ignored errors won't show in the default list. They'll auto-reopen if seen again."
                : "Optional: add a note about how you fixed it."}
            </div>
            <Textarea
              placeholder="Note (optional)…"
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResolveOpen(false)}>Cancel</Button>
            <Button onClick={performResolve}>
              {resolveAction === "ignore" ? "Ignore" : "Resolve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ label, value, accent }) => (
  <Card className="bg-zinc-900/40 border-zinc-800">
    <CardContent className="pt-4 pb-3">
      <div className="text-[11px] text-zinc-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-semibold ${accent || "text-zinc-50"}`}>{value ?? 0}</div>
    </CardContent>
  </Card>
);

const ErrorDetail = ({ detail, onCopyForClaude, onCopyText, onResolve, onIgnore, onReopen, onDelete }) => {
  const latest = detail.latest_occurrence || {};
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={detail.status} />
          <SourceBadge source={detail.source} />
          <Badge className="bg-zinc-800 text-zinc-300 border-0 text-[10px]">
            {detail.count}× seen
          </Badge>
          <span className="text-[11px] text-zinc-500 font-mono">
            fingerprint: {detail.fingerprint}
          </span>
        </div>
        <div className="text-base text-zinc-50 font-medium break-words">
          {detail.message}
        </div>
        <div className="text-xs text-zinc-500">
          First seen: {formatDateTime(detail.first_seen)} · Last seen: {formatDateTime(detail.last_seen)}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={onCopyForClaude} className="bg-orange-500 hover:bg-orange-600 text-white">
          <Copy className="h-4 w-4 mr-1.5" /> Copy for Claude
        </Button>
        {detail.status === "new" ? (
          <>
            <Button variant="outline" onClick={onResolve}>
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Resolved
            </Button>
            <Button variant="outline" onClick={onIgnore}>
              <EyeOff className="h-4 w-4 mr-1.5" /> Ignore
            </Button>
          </>
        ) : (
          <Button variant="outline" onClick={onReopen}>
            <RotateCcw className="h-4 w-4 mr-1.5" /> Reopen
          </Button>
        )}
        <Button variant="outline" onClick={onDelete} className="text-red-300 border-red-500/30 hover:bg-red-500/10 ml-auto">
          <Trash2 className="h-4 w-4 mr-1.5" /> Delete
        </Button>
      </div>

      {/* Resolution note */}
      {detail.resolution_note && (
        <div className="p-3 rounded-md bg-zinc-800/50 border border-zinc-700 text-sm">
          <div className="text-[11px] text-zinc-500 uppercase mb-1">Resolution note</div>
          <div className="text-zinc-200 whitespace-pre-wrap">{detail.resolution_note}</div>
          {detail.resolved_by && (
            <div className="text-[11px] text-zinc-500 mt-1">
              by {detail.resolved_by} · {formatDateTime(detail.resolved_at)}
            </div>
          )}
        </div>
      )}

      {/* Latest occurrence context */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        {latest.method && latest.url && (
          <DetailRow label="Request" value={`${latest.method} ${latest.url}`} mono onCopy={onCopyText} />
        )}
        {latest.status_code != null && (
          <DetailRow label="Status code" value={String(latest.status_code)} />
        )}
        {latest.user_email && (
          <DetailRow
            label="User"
            value={`${latest.user_email}${latest.user_role ? ` (${latest.user_role})` : ""}`}
          />
        )}
        {latest.user_agent && (
          <DetailRow label="User agent" value={latest.user_agent} mono onCopy={onCopyText} />
        )}
      </div>

      {/* Extra */}
      {latest.extra && Object.keys(latest.extra).length > 0 && (
        <CodeBlock
          title="Extra context"
          code={JSON.stringify(latest.extra, null, 2)}
          onCopy={() => onCopyText(JSON.stringify(latest.extra, null, 2), "Extra copied")}
        />
      )}

      {/* Request body */}
      {latest.request_body && (
        <CodeBlock
          title="Request body"
          code={latest.request_body}
          onCopy={() => onCopyText(latest.request_body, "Body copied")}
        />
      )}

      {/* Stack trace */}
      {detail.stack_trace && (
        <CodeBlock
          title="Stack trace"
          code={detail.stack_trace}
          onCopy={() => onCopyText(detail.stack_trace, "Stack copied")}
          maxHeight="max-h-96"
        />
      )}

      {/* Recent occurrences */}
      {detail.recent_occurrences && detail.recent_occurrences.length > 1 && (
        <div>
          <div className="text-[11px] text-zinc-500 uppercase mb-2">
            Recent occurrences ({detail.recent_occurrences.length})
          </div>
          <div className="border border-zinc-800 rounded-md divide-y divide-zinc-800 text-xs">
            {detail.recent_occurrences.slice().reverse().map((o, i) => (
              <div key={i} className="px-3 py-2 flex items-center gap-2 flex-wrap">
                <span className="text-zinc-400">{formatDateTime(o.at)}</span>
                {o.method && o.url && (
                  <span className="font-mono text-zinc-500">{o.method} {o.url}</span>
                )}
                {o.user_email && <span className="text-zinc-500">· {o.user_email}</span>}
                {o.status_code != null && <span className="text-amber-300">· {o.status_code}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value, mono, onCopy }) => (
  <div className="flex items-start gap-2">
    <div className="text-[11px] text-zinc-500 uppercase w-24 flex-shrink-0 pt-0.5">{label}</div>
    <div className={`text-zinc-200 break-all flex-1 ${mono ? "font-mono text-xs" : ""}`}>
      {value}
    </div>
    {onCopy && (
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-1.5"
        onClick={() => onCopy(value)}
        title="Copy"
      >
        <Copy className="h-3 w-3" />
      </Button>
    )}
  </div>
);

const CodeBlock = ({ title, code, onCopy, maxHeight = "max-h-64" }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <div className="text-[11px] text-zinc-500 uppercase">{title}</div>
      {onCopy && (
        <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={onCopy}>
          <Copy className="h-3 w-3 mr-1" /> Copy
        </Button>
      )}
    </div>
    <pre className={`bg-zinc-950 border border-zinc-800 rounded-md p-3 text-xs text-zinc-300 overflow-auto ${maxHeight} whitespace-pre-wrap break-all`}>
      {code}
    </pre>
  </div>
);

export default ErrorLogsPage;
